import type { QueryClient } from '@tanstack/react-query';
import { campaignKeys, type RosterTab, type ThreadType } from '../queryKeys';

type RosterEntry = Record<string, unknown> & { id?: string };
type RosterPage = Record<string, unknown> & { entries?: RosterEntry[] };

/**
 * Which roster tab's row carries a given chat thread's unread_count. Each tab's
 * row exposes a DIFFERENT thread's count under the same `unread_count` field
 * (Creators = selection, Scripts = script, Content = content), so an SSE or
 * mark-read patch MUST target only the owning tab — otherwise a script/content
 * event (or its mark-read echo of 0) clobbers another tab's badge for the same
 * entry. The reconcile invalidate still reorders cross-tab by recency.
 */
export const TAB_FOR_THREAD: Record<ThreadType, RosterTab> = {
  selection: 'creators',
  script: 'scripts',
  content: 'content',
};

/**
 * In-place edits to the cached roster list (no refetch), for instant SSE-driven
 * UI updates that the next list refetch reconciles. Each guards the page shape
 * and returns the original reference when there's nothing to change, so
 * unrelated caches (per-entry detail/analytics) are never touched.
 *
 * `tab` scopes setQueriesData to one tab's pages (['roster', id, tab, *]); when
 * omitted it spans every roster page for the campaign (['roster', id, *]).
 */
function rosterScope(campaignId: string, tab?: RosterTab) {
  return tab ? ([...campaignKeys.rosterAll(campaignId), tab] as const) : campaignKeys.rosterAll(campaignId);
}

/** Patch a single roster entry (matched by `id`) across the scoped pages. */
export function patchRosterEntry(
  qc: QueryClient,
  campaignId: string,
  entryId: string,
  patch: (entry: RosterEntry) => RosterEntry,
  tab?: RosterTab,
): void {
  qc.setQueriesData({ queryKey: rosterScope(campaignId, tab) }, (oldData: unknown) => {
    if (!oldData || typeof oldData !== 'object') return oldData;
    const data = oldData as RosterPage;
    if (!Array.isArray(data.entries)) return oldData;
    return { ...data, entries: data.entries.map((e) => (e.id === entryId ? patch(e) : e)) };
  });
}

/**
 * Float a roster entry (matched by `id`) to the top of the scoped pages —
 * optimistic WhatsApp-style recency reorder on a new chat message, so the row
 * jumps immediately instead of after a list refetch. The server's
 * last_activity_at sort reconciles on the next refetch (and handles cross-page
 * promotion the in-cache splice can't).
 */
export function bumpRosterEntryToTop(
  qc: QueryClient,
  campaignId: string,
  entryId: string,
  tab?: RosterTab,
): void {
  qc.setQueriesData({ queryKey: rosterScope(campaignId, tab) }, (oldData: unknown) => {
    if (!oldData || typeof oldData !== 'object') return oldData;
    const data = oldData as RosterPage;
    if (!Array.isArray(data.entries)) return oldData;
    const idx = data.entries.findIndex((e) => e.id === entryId);
    if (idx <= 0) return oldData; // absent, or already first — nothing to do
    const entries = data.entries.slice();
    const [moved] = entries.splice(idx, 1);
    if (!moved) return oldData;
    entries.unshift(moved);
    return { ...data, entries };
  });
}
